import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Menu, X } from 'lucide-react';
import { personalInfo } from '../mock';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: './about', id: 'about' },
    { label: './skills', id: 'skills' },
    { label: './projects', id: 'projects' },
    { label: './timeline', id: 'timeline' },
    { label: './contact', id: 'contact' }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <header style={{
        background: scrolled ? 'rgba(0, 0, 0, 0.95)' : '#000000',
        borderBottom: `1px solid ${scrolled ? 'rgba(0, 255, 209, 0.3)' : 'rgba(255, 255, 255, 0.25)'}`,
        padding: '16px 7.6923%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'fixed',
        top: 0,
        width: '100%',
        height: '80px',
        zIndex: 100,
        boxSizing: 'border-box',
        backdropFilter: scrolled ? 'blur(10px)' : 'none',
        transition: 'all 0.3s ease'
      }}>
        {/* Logo */}
        <div 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            fontFamily: 'monospace',
            fontSize: '24px',
            fontWeight: 700,
            color: '#00FFD1',
            cursor: 'pointer',
            textShadow: '0 0 10px rgba(0, 255, 209, 0.5)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textShadow = '0 0 20px rgba(0, 255, 209, 0.8)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textShadow = '0 0 10px rgba(0, 255, 209, 0.5)';
          }}
        >
          {'<GOLDHAT />'}
        </div>

        {/* Desktop Navigation */}
        <nav style={{
          display: 'flex',
          alignItems: 'center',
          gap: '32px'
        }} className="desktop-nav">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              style={{
                background: 'none',
                border: 'none',
                color: '#4D4D4D',
                fontSize: '18px',
                fontWeight: 400,
                cursor: 'pointer',
                transition: 'color 0.3s ease',
                fontFamily: 'monospace'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#FFFFFF';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#4D4D4D';
              }}
            >
              {item.label}
            </button>
          ))}

          {/* Social links */}
          <div style={{ display: 'flex', gap: '16px', marginLeft: '16px' }}>
            <a
              href={personalInfo.github}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#4D4D4D',
                transition: 'color 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#00FFD1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#4D4D4D';
              }}
            >
              <Github size={20} />
            </a>
            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#4D4D4D',
                transition: 'color 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#00FFD1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#4D4D4D';
              }}
            >
              <Linkedin size={20} />
            </a>
          </div>
        </nav>

        {/* Mobile menu button */}
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            color: '#00FFD1',
            cursor: 'pointer'
          }}
        >
          {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu" style={{
          position: 'fixed',
          top: '80px',
          left: 0,
          width: '100%',
          background: 'rgba(0, 0, 0, 0.98)',
          backdropFilter: 'blur(10px)',
          zIndex: 99,
          padding: '32px 20px',
          borderBottom: '1px solid rgba(0, 255, 209, 0.3)',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              style={{
                display: 'block',
                width: '100%',
                background: 'none',
                border: 'none',
                color: '#FFFFFF',
                fontSize: '20px',
                fontWeight: 400,
                cursor: 'pointer',
                padding: '16px 0',
                textAlign: 'left',
                fontFamily: 'monospace',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#00FFD1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#FFFFFF';
              }}
            >
              {item.label}
            </button>
          ))}

          {/* Mobile social links */}
          <div style={{ display: 'flex', gap: '24px', marginTop: '32px', justifyContent: 'center' }}>
            <a
              href={personalInfo.github}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#00FFD1' }}
            >
              <Github size={28} />
            </a>
            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#00FFD1' }}
            >
              <Linkedin size={28} />
            </a>
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default Header;